"use client";

import { getAuthSubscription } from "@/app/(home)/(protected)/settings/subscription/actions";
import { ErrorType } from "@/app/api/utils/api-response";
import { useAuth } from "@/components/auth-provider";
import { AppRoutes, NavigationConfig } from "@/config/navigation-config";
import { SUBSCRIPTION_QUERY_KEY } from "@/constants/subscriptions";
import { PortalName, usePortals } from "@/context/portal-context";
import handleErrors from "@/lib/handle-errors";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { useCallback, useMemo } from "react";
import { toast } from "sonner";

const queryFn = async () => {
  const { subscription, error } = await getAuthSubscription();
  if (error) {
    throw error;
  }

  return subscription;
};

export function useSubscription() {
  const { claims } = useAuth();

  const { data: subscription, isLoading, refetch } = useQuery({
    queryKey: SUBSCRIPTION_QUERY_KEY,
    queryFn,
    staleTime: 1,
    refetchOnMount: true,
  });

  const { openPortal } = usePortals();
  const router = useRouter();
  const plan = subscription?.plan ?? null;
  const hasActiveSubscription = subscription != null &&
    subscription.status === "active";

  const hasSubscriptionErrors = useMemo(() => {
    return !subscription || subscription.status !== "active" ||
      subscription.credits <= 0;
  }, [subscription]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSubscriptionError = useCallback((error?: any) => {
    if (!error) {
      if (!subscription || subscription.status !== "active") {
        openPortal(PortalName.pricing_modal);
      } else if (subscription.credits <= 0) {
        toast.error("No credits found", {
          description: "Buy more credits to continue using the chat.",
          action: {
            label: "Buy credits",
            onClick: () =>
              router.push(
                NavigationConfig.getPath(AppRoutes.SUBSCRIPTION_PLANS, {
                  hash: "token-packs",
                }),
              ),
          },
        });
      }
    } else if (error.code === ErrorType.NO_SUBSCRIPTION_FOUND) {
      openPortal(PortalName.pricing_modal);
    } else if (error.code === ErrorType.NO_CREDITS_FOUND) {
      toast.error("No credits found", {
        description: "Buy more credits to continue using the chat.",
        action: {
          label: "Buy credits",
          onClick: () =>
            router.push(NavigationConfig.getPath(AppRoutes.SUBSCRIPTION_PLANS, {
              hash: "token-packs",
            })),
        },
      });
    } else {
      handleErrors(error);
    }
  }, [openPortal, router, subscription]);

  return {
    subscription,
    plan,
    hasActiveSubscription,
    loadingSubscription: isLoading,
    refetchSubscription: refetch,
    hasSubscriptionErrors,
    handleSubscriptionError,
    isAnonymousWithoutSubscription: claims?.is_anonymous,
  };
}
